import { useState, type ReactNode } from "react"
import { useMutation } from "@tanstack/react-query"
import { Select as SelectPrimitive } from "@base-ui/react/select"
import {
  RiArrowDownSLine,
  RiArrowRightSLine,
  RiCheckLine,
} from "@remixicon/react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem } from "@/components/ui/select"
import { cn } from "@/lib/utils"
import type { Textbook, Chapter } from "@/lib/api/textbooks"
import { submitFeedback } from "@/lib/api/feedback"
import { cumulativeCounts } from "@/hooks/use-progress-selection"

function MissingTextbookPrompt({
  buttonClassName = "mt-[-20px] ml-[22px]",
}: {
  buttonClassName?: string
}) {
  const [open, setOpen] = useState(false)
  const [text, setText] = useState("")

  const { mutate, isPending, isSuccess } = useMutation({
    mutationFn: () => submitFeedback("missing_textbook", { text: text.trim() }),
  })

  if (isSuccess) {
    return (
      <p className="mb-4 text-sm text-muted-foreground">
        Thanks! We'll look into adding it.
      </p>
    )
  }

  return (
    <div className="mt-n-10 mb-4">
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={cn(
            "text-xs text-muted-foreground/70 underline-offset-4 hover:underline",
            buttonClassName
          )}
        >
          Don't see your textbook? Let us know
        </button>
      ) : (
        <div className="flex items-center gap-2">
          <Input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Which textbook are you using?"
            className="w-full max-w-sm"
            autoFocus
            disabled={isPending}
            onKeyDown={(e) => {
              if (e.key === "Enter" && text.trim()) mutate()
              if (e.key === "Escape") setOpen(false)
            }}
          />
          <Button
            size="sm"
            onClick={() => mutate()}
            disabled={isPending || !text.trim()}
          >
            Send
          </Button>
        </div>
      )}
    </div>
  )
}

type ProgressPickerProps = {
  textbooks: Textbook[]
  selectedTextbookId: string | null
  onTextbookChange: (id: string) => void
  chapters: Chapter[]
  selectedChapterNumber: number | null
  onChapterSelect: (chapterNumber: number) => void
  heading?: ReactNode
  description?: ReactNode
  footer?: ReactNode
}

const MONO_LABEL = "font-mono uppercase tracking-[0.2em]"

function SectionLabel({
  number,
  title,
  description,
}: {
  number: string
  title: string
  description?: string
}) {
  return (
    <div className="mb-4">
      <div className="flex items-baseline gap-3">
        <span className="font-mono text-sm font-medium text-primary">
          {number}
        </span>
        <h2 className="text-2xl font-semibold text-[var(--label)]">{title}</h2>
      </div>
      {description && (
        <p className="mt-1.5 text-muted-foreground">{description}</p>
      )}
    </div>
  )
}

function TextbookSelectV2({
  textbooks,
  selectedTextbookId,
  onTextbookChange,
  chapters,
}: {
  textbooks: Textbook[]
  selectedTextbookId: string | null
  onTextbookChange: (id: string) => void
  chapters: Chapter[]
}) {
  const selected = textbooks.find((t) => t.id === selectedTextbookId)
  const totals = chapters.reduce(
    (acc, c) => ({
      vocab: acc.vocab + c.wordCount,
      kanji: acc.kanji + c.kanjiCount,
    }),
    { vocab: 0, kanji: 0 }
  )
  const subtitle =
    selected && chapters.length > 0
      ? `${chapters.length} chapters · ${totals.vocab} vocab · ${totals.kanji} kanji`
      : null

  return (
    <Select
      value={selectedTextbookId ?? undefined}
      onValueChange={(value) => value && onTextbookChange(value)}
    >
      <SelectPrimitive.Trigger
        data-slot="select-trigger"
        aria-label="Textbook"
        className="flex w-full items-center justify-between gap-4 rounded-[var(--surface-radius)] border border-[var(--surface-hairline)] bg-[var(--surface-card)] px-5 py-4 text-left transition-colors outline-none hover:bg-muted/30 focus-visible:border-ring data-[popup-open]:border-ring sm:w-2/3"
      >
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-base font-medium text-[var(--label)]">
            {selected?.title ?? "Select…"}
          </span>
          {subtitle && (
            <span
              className={cn(
                MONO_LABEL,
                "mt-[14px] truncate text-[9px] tracking-normal text-muted-foreground sm:text-[11px] sm:tracking-[0.08em]"
              )}
            >
              {subtitle}
            </span>
          )}
        </span>
        <SelectPrimitive.Icon
          render={
            <RiArrowDownSLine className="size-5 shrink-0 text-muted-foreground" />
          }
        />
      </SelectPrimitive.Trigger>
      <SelectContent className="rounded-[var(--surface-radius)]">
        {textbooks
          .filter((t) => t.active)
          .map((t) => (
            <SelectItem key={t.id} value={t.id}>
              {t.title}
            </SelectItem>
          ))}
      </SelectContent>
    </Select>
  )
}

function MetaCount({ value, label }: { value: number; label: string }) {
  return (
    <span className="font-mono text-sm">
      <span className="font-bold text-[var(--label)]">{value}</span>{" "}
      <span className="text-muted-foreground">{label}</span>
    </span>
  )
}

function ChapterTimelineRow({
  chapter,
  known,
  nextKnown,
  isFirst,
  isLast,
  selected,
  footer,
  onSelect,
}: {
  chapter: Chapter
  known: boolean
  // Whether the following chapter is also known — keeps the segment below the
  // last selected dot un-highlighted.
  nextKnown: boolean
  isFirst: boolean
  isLast: boolean
  selected: boolean
  // Action (e.g. Save) shown on the currently-selected chapter, anchored to
  // the bottom-right of the counts column.
  footer?: ReactNode
  onSelect: () => void
}) {
  return (
    <li className="relative">
      <button
        type="button"
        onClick={onSelect}
        aria-label={`Set your progress to chapter ${chapter.number}`}
        className={cn(
          "group grid w-full grid-cols-[1.5rem_1fr] items-start gap-x-4 px-5 text-left transition-colors hover:bg-muted/40 sm:grid-cols-[1.5rem_1fr_auto]",
          known && "bg-[var(--selected)] hover:bg-[var(--selected-hover)]"
        )}
      >
        <span className="relative flex justify-center self-stretch" aria-hidden>
          {!isFirst && (
            <span
              className={cn(
                "absolute top-0 left-1/2 h-[2.375rem] w-0.5 -translate-x-1/2",
                known
                  ? "bg-[var(--timeline-line)]"
                  : "bg-[var(--timeline-line-muted)]"
              )}
            />
          )}
          {!isLast && (
            <span
              className={cn(
                "absolute top-[2.375rem] bottom-0 left-1/2 w-0.5 -translate-x-1/2",
                nextKnown
                  ? "bg-[var(--timeline-line)]"
                  : "bg-[var(--timeline-line-muted)]"
              )}
            />
          )}
          <span
            className={cn(
              "absolute top-[2.375rem] left-1/2 z-10 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2",
              known
                ? "border-[var(--timeline-line)] bg-[var(--timeline-line)]"
                : "border-[var(--timeline-line-muted)] bg-[var(--surface-card)]"
            )}
          />
        </span>

        <span className="min-w-0 py-6">
          <span className="flex flex-col sm:flex-row sm:items-baseline sm:gap-3">
            <span
              className={cn(
                MONO_LABEL,
                "mt-2 mb-4 text-sm text-muted-foreground sm:my-0"
              )}
            >
              CH {String(chapter.number).padStart(2, "0")}
            </span>
            <span className="text-lg font-medium text-[var(--label)]">
              {chapter.title}
            </span>
          </span>
          {/* Counts live below the title on mobile; on sm+ they move to the
              third grid column (see below). */}
          <span className="mt-2 flex gap-3 sm:hidden">
            <MetaCount value={chapter.wordCount} label="vocabulary" />
            <MetaCount value={chapter.kanjiCount} label="kanji" />
          </span>
          {chapter.description && (
            <span className="mt-1.5 block leading-relaxed text-muted-foreground">
              {chapter.description}
            </span>
          )}
        </span>

        <span className="hidden shrink-0 flex-col items-end gap-1 py-6 pl-4 sm:flex">
          <MetaCount value={chapter.wordCount} label="vocabulary" />
          <MetaCount value={chapter.kanjiCount} label="kanji" />
        </span>
      </button>
      {footer ? (
        <div className="absolute top-6 right-5 z-10 animate-in duration-300 fade-in-0 slide-in-from-bottom-1 sm:top-auto sm:bottom-6">
          {footer}
        </div>
      ) : (
        !selected && (
          <div className="absolute top-6 right-5 z-10 sm:hidden">
            <Button variant="outline" onClick={onSelect}>
              Select
            </Button>
          </div>
        )
      )}
    </li>
  )
}

function ChapterTimeline({
  chapters,
  selectedUpTo,
  onSelect,
  footer,
}: {
  chapters: Chapter[]
  selectedUpTo: number | null
  onSelect: (chapterNumber: number) => void
  footer?: ReactNode
}) {
  const isKnown = (n: number) => selectedUpTo !== null && n <= selectedUpTo
  const knownCount = chapters.filter((c) => isKnown(c.number)).length
  const lastKnown =
    chapters.length > 0 && isKnown(chapters[chapters.length - 1].number)

  return (
    <div className="overflow-hidden rounded-[var(--surface-radius)] border border-[var(--surface-hairline)] bg-[var(--surface-card)]">
      <header
        className={cn(
          "flex items-center justify-between border-b border-[var(--surface-hairline)] px-5 py-6",
          knownCount > 0 && "bg-[var(--selected)]"
        )}
      >
        <span
          className={cn(MONO_LABEL, "text-sm font-medium text-[var(--label)]")}
        >
          Beginning
        </span>
        <span className="font-mono text-sm font-medium text-[var(--label)] tabular-nums">
          {knownCount} / {chapters.length}
        </span>
      </header>

      <ol>
        {chapters.map((chapter, index) => (
          <ChapterTimelineRow
            key={chapter.id}
            chapter={chapter}
            known={isKnown(chapter.number)}
            nextKnown={isKnown(chapter.number + 1)}
            isFirst={index === 0}
            isLast={index === chapters.length - 1}
            selected={chapter.number === selectedUpTo}
            footer={chapter.number === selectedUpTo ? footer : undefined}
            onSelect={() => onSelect(chapter.number)}
          />
        ))}
      </ol>

      <div
        className={cn(
          "grid grid-cols-[1.5rem_1fr] items-start gap-x-4 px-5 pt-0 pb-8",
          lastKnown && "bg-[var(--selected)]"
        )}
      >
        <span aria-hidden />
        <span className={cn(MONO_LABEL, "text-sm text-muted-foreground")}>
          End
        </span>
      </div>
    </div>
  )
}

export function ProgressPicker({
  textbooks,
  selectedTextbookId,
  onTextbookChange,
  chapters,
  selectedChapterNumber,
  onChapterSelect,
  heading,
  description,
  footer,
}: ProgressPickerProps) {
  return (
    <div className="pb-12">
      {heading && (
        <h1 className="mb-2 text-[40px] leading-[1.05] font-semibold sm:text-[56px] sm:leading-tight">
          {heading}
        </h1>
      )}
      {description && (
        <p className="mb-8 w-full text-base text-foreground sm:w-1/2">
          {description}
        </p>
      )}

      <SectionLabel number="01" title="Textbook" />
      <TextbookSelectV2
        textbooks={textbooks}
        selectedTextbookId={selectedTextbookId}
        onTextbookChange={onTextbookChange}
        chapters={chapters}
      />

      <div className="mt-3">
        <MissingTextbookPrompt />
      </div>

      <div className="mt-10">
        <SectionLabel
          number="02"
          title="Chapter"
          description="Select the chapter you're currently on. Stories will use only the vocabulary and grammar up to and including it."
        />
      </div>
      {selectedTextbookId && chapters.length > 0 ? (
        <ChapterTimeline
          key={selectedTextbookId}
          chapters={chapters}
          selectedUpTo={selectedChapterNumber}
          onSelect={onChapterSelect}
          footer={footer}
        />
      ) : (
        <div className="rounded-[var(--surface-radius)] border border-dashed border-[var(--surface-hairline)] bg-[var(--surface-card)] px-5 py-12 text-center text-muted-foreground">
          Pick a textbook above and chapters will appear here
        </div>
      )}
    </div>
  )
}

// Phone layout: iOS-style grouped list.

function GroupLabel({ children }: { children: ReactNode }) {
  return (
    <h2 className="pl-4 text-[13px] text-muted-foreground uppercase">
      {children}
    </h2>
  )
}

function TextbookRow({
  textbooks,
  selectedTextbookId,
  onTextbookChange,
}: {
  textbooks: Textbook[]
  selectedTextbookId: string | null
  onTextbookChange: (id: string) => void
}) {
  const selected = textbooks.find((t) => t.id === selectedTextbookId)

  return (
    <Select
      value={selectedTextbookId ?? undefined}
      onValueChange={(value) => value && onTextbookChange(value)}
    >
      <SelectPrimitive.Trigger
        data-slot="select-trigger"
        aria-label="Textbook"
        className="flex min-h-12 w-full items-center gap-2 rounded-xl bg-[var(--surface-card)] px-4 text-left text-[17px] text-[var(--label)] outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="flex-1 truncate">{selected?.title ?? "Select…"}</span>
        <SelectPrimitive.Icon
          render={
            <RiArrowRightSLine className="size-5 shrink-0 text-muted-foreground/60" />
          }
        />
      </SelectPrimitive.Trigger>
      <SelectContent className="rounded-xl">
        {textbooks
          .filter((t) => t.active)
          .map((t) => (
            <SelectItem key={t.id} value={t.id}>
              {t.title}
            </SelectItem>
          ))}
      </SelectContent>
    </Select>
  )
}

function ChapterListRow({
  chapter,
  checked,
  chapters,
  onSelect,
}: {
  chapter: Chapter
  checked: boolean
  chapters: Chapter[]
  onSelect: () => void
}) {
  const totals = checked ? cumulativeCounts(chapters, chapter.number) : null

  return (
    <li className="border-b border-[var(--surface-hairline)] last:border-b-0">
      <button
        type="button"
        role="radio"
        aria-checked={checked}
        onClick={onSelect}
        className="flex min-h-11 w-full items-center gap-3 px-4 py-2.5 text-left text-[17px] text-[var(--label)] outline-none focus-visible:bg-muted/40"
      >
        <span className="w-[22px] shrink-0 text-[15px] text-muted-foreground tabular-nums">
          {chapter.number}
        </span>
        <span className="flex-1">{chapter.title}</span>
        {checked && (
          <RiCheckLine className="size-5 shrink-0 text-primary" aria-hidden />
        )}
      </button>
      {totals && (
        <div className="flex flex-col gap-2 pr-4 pb-3.5 pl-[50px]">
          {chapter.description && (
            <p className="text-[15px] leading-[1.45] text-muted-foreground">
              {chapter.description}
            </p>
          )}
          <span className="font-mono text-[13px] text-muted-foreground">
            <strong className="text-[var(--label)]">{totals.wordCount}</strong>{" "}
            vocabulary ·{" "}
            <strong className="text-[var(--label)]">{totals.kanjiCount}</strong>{" "}
            kanji
          </span>
        </div>
      )}
    </li>
  )
}

export function ProgressList({
  textbooks,
  selectedTextbookId,
  onTextbookChange,
  chapters,
  selectedChapterNumber,
  onChapterSelect,
}: Omit<ProgressPickerProps, "heading" | "description" | "footer">) {
  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-2">
        <GroupLabel>Textbook</GroupLabel>
        <TextbookRow
          textbooks={textbooks}
          selectedTextbookId={selectedTextbookId}
          onTextbookChange={onTextbookChange}
        />
        <MissingTextbookPrompt buttonClassName="ml-4" />
      </section>

      <section className="flex flex-col gap-2">
        <GroupLabel>I've studied up to</GroupLabel>
        {selectedTextbookId && chapters.length > 0 ? (
          <ul
            role="radiogroup"
            aria-label="Chapter"
            className="overflow-hidden rounded-xl bg-[var(--surface-card)]"
          >
            {chapters.map((chapter) => (
              <ChapterListRow
                key={chapter.id}
                chapter={chapter}
                checked={chapter.number === selectedChapterNumber}
                chapters={chapters}
                onSelect={() => onChapterSelect(chapter.number)}
              />
            ))}
          </ul>
        ) : (
          <div className="rounded-xl bg-[var(--surface-card)] px-4 py-8 text-center text-[15px] text-muted-foreground">
            Pick a textbook above and chapters will appear here
          </div>
        )}
        <p className="px-4 text-[13px] leading-[1.4] text-muted-foreground">
          New stories only use grammar and vocabulary up to this lesson.
        </p>
      </section>
    </div>
  )
}
